import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  makeStyles,
} from "@material-ui/core";
import { MoreVert } from "@material-ui/icons";

import { useTranslation } from "../hooks/useTranslation";
import { requiresHumanAction } from "../utils/jobTaskStatus";

/**
 * Renderiza a coluna de ações de uma linha de job com hierarquia visual clara:
 *
 *  [ Ação primária (botão) ]  |  [ ícones secundários ]  |  [ ⋮ menu ]
 *
 * As ações vêm de `actions` (array). Cada ação aceita:
 *   - key:     identificador da ação
 *   - icon:    elemento JSX
 *   - tooltip: texto curto (mostrado no hover) — string ou (job) => string
 *   - label:   texto longo (usado no botão primário e nos itens do menu) —
 *              string ou (job) => string
 *   - tier:    "primary" | "secondary" | "menu" | "danger" (default "secondary")
 *   - hidden:  (job) => boolean. quando true, a ação é omitida
 *   - disabled:(job) => boolean
 *   - onClick: (job) => void (override do onAction)
 *
 * Regras de exibição
 *   - apenas a PRIMEIRA ação `tier=primary` visível é exibida como botão
 *     destacado. as demais primárias visíveis caem para `secondary`.
 *   - se o job está em estado que demanda ação humana e não há `primary`
 *     visível, o botão fica "vazio" — o destaque vai para o status chip.
 *   - `secondary` aparece inline como IconButton neutro.
 *   - `menu` e `danger` ficam dentro do menu "⋮", separados por divider.
 */
const useStyles = makeStyles((theme) => ({
  root: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexWrap: "nowrap",
  },
  primaryButton: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.75rem",
    lineHeight: 1.2,
    padding: theme.spacing(0.5, 1.25),
    minHeight: 30,
    boxShadow: "none",
    backgroundColor: "var(--ikaro-purple-accent)",
    color: "var(--ikaro-text-white)",
    "&:hover": {
      backgroundColor: "var(--ikaro-purple-hover)",
      boxShadow: "var(--ikaro-shadow-light)",
    },
    "& .MuiButton-startIcon": {
      marginRight: theme.spacing(0.5),
    },
    "& .MuiButton-startIcon .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
  },
  primaryButtonHumanAction: {
    backgroundColor: "var(--ikaro-purple-accent)",
    "&:hover": {
      backgroundColor: "var(--ikaro-purple-hover)",
    },
    /* Pulso suave para sinalizar que existe ação humana pendente sem
       virar piscar incômodo quando há muitos itens no estado. */
    animation: "$pulseGlow 3.2s ease-in-out infinite",
  },
  /* Variante discreta para ações primárias de **consulta** (ex.: "Visualizar
     referências"). Mantém a cor de marca para indicar que é a ação principal
     da linha, mas com aparência outlined — sem o "convite à ação" do botão
     contained, já que o processamento já terminou e não há pendência humana. */
  primaryButtonGhost: {
    backgroundColor: "transparent",
    color: "var(--ikaro-purple-accent)",
    border: "1px solid var(--ikaro-purple-accent)",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "var(--ikaro-purple-light)",
      borderColor: "var(--ikaro-purple-accent)",
      boxShadow: "none",
    },
    '[data-theme="dark"] &': {
      color: "#c4a4e0",
      borderColor: "#c4a4e0",
      "&:hover": {
        backgroundColor: "var(--ikaro-purple-light)",
      },
    },
  },
  primaryButtonDownload: {
    backgroundColor: "var(--ikaro-orange-primary)",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-hover)",
    },
  },
  primaryButtonRetry: {
    backgroundColor: "var(--ikaro-status-warning-text)",
    "&:hover": {
      backgroundColor: "var(--ikaro-status-warning)",
      color: "var(--ikaro-text-white)",
    },
  },
  "@keyframes pulseGlow": {
    "0%, 100%": {
      boxShadow: "0 0 0 0 rgba(100, 49, 132, 0.35)",
    },
    "50%": {
      boxShadow: "0 0 0 5px rgba(100, 49, 132, 0)",
    },
  },
  secondaryIconButton: {
    padding: 6,
    color: "var(--ikaro-gray-dark)",
    "&:hover": {
      backgroundColor: "var(--ikaro-bg-surface-hover)",
      color: "var(--ikaro-text-primary)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.05rem",
    },
  },
  divider: {
    height: 18,
    margin: theme.spacing(0, 0.25),
    backgroundColor: "var(--ikaro-border-default)",
  },
  menuButton: {
    padding: 6,
    color: "var(--ikaro-gray-dark)",
    "&:hover": {
      backgroundColor: "var(--ikaro-bg-surface-hover)",
      color: "var(--ikaro-text-primary)",
    },
  },
  menuPaper: {
    minWidth: 200,
    boxShadow: "var(--ikaro-shadow-medium)",
    border: "1px solid var(--ikaro-border-default)",
  },
  menuItem: {
    fontSize: "0.85rem",
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    "& .MuiListItemIcon-root": {
      minWidth: 32,
      color: "var(--ikaro-gray-dark)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.1rem",
    },
  },
  menuItemDanger: {
    color: "var(--ikaro-status-error-text)",
    "& .MuiListItemIcon-root": {
      color: "var(--ikaro-status-error-text)",
    },
    "&:hover": {
      backgroundColor: "var(--ikaro-status-error-bg)",
    },
  },
}));

const DEFAULT_TIER = "secondary";

const normalizeTier = (action) => {
  const t = action?.tier;
  if (t === "primary" || t === "secondary" || t === "menu" || t === "danger") {
    return t;
  }
  return DEFAULT_TIER;
};

const primaryButtonClassByKey = (key, classes) => {
  if (!key) return null;
  if (key === "download") return classes.primaryButtonDownload;
  if (key === "reprocess") return classes.primaryButtonRetry;
  return null;
};

const JobActionsCell = ({ job, actions = [], onAction }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const [menuAnchor, setMenuAnchor] = useState(null);

  const visibleActions = useMemo(() => {
    return actions
      .filter((a) => a && (!a.hidden || !a.hidden(job)))
      .map((a, idx) => ({
        ...a,
        _tier: normalizeTier(a),
        _key: a.key || `action-${idx}`,
      }));
  }, [actions, job]);

  const { primary, secondary, menuItems } = useMemo(() => {
    let pickedPrimary = null;
    const sec = [];
    const menu = [];

    for (const a of visibleActions) {
      if (a._tier === "primary" && !pickedPrimary) {
        pickedPrimary = a;
        continue;
      }
      if (a._tier === "primary") {
        sec.push(a);
        continue;
      }
      if (a._tier === "menu" || a._tier === "danger") {
        menu.push(a);
        continue;
      }
      sec.push(a);
    }

    return { primary: pickedPrimary, secondary: sec, menuItems: menu };
  }, [visibleActions]);

  const handleClick = (e, action) => {
    e.stopPropagation();
    if (action.disabled && action.disabled(job)) return;
    if (action.onClick) {
      action.onClick(job);
    } else if (onAction) {
      onAction(action.key, job);
    }
  };

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    e?.stopPropagation?.();
    setMenuAnchor(null);
  };

  const handleMenuItemClick = (e, action) => {
    handleClick(e, action);
    handleMenuClose(e);
  };

  if (!primary && secondary.length === 0 && menuItems.length === 0) {
    return null;
  }

  const isHumanAction = requiresHumanAction(job);
  const primaryKeyClass = primary
    ? primaryButtonClassByKey(primary.key, classes)
    : null;

  /* `label`, `tooltip` e `variant` aceitam string ou função `(job) => valor`
     para permitir rótulos/estilos dinâmicos (ex.: variar o texto conforme
     task/status, ou alternar entre "contained" e "ghost" quando o botão
     deixa de ser uma ação pendente e vira só consulta). */
  const resolveDynamic = (value, jobArg) =>
    typeof value === "function" ? value(jobArg) : value;

  const primaryLabel =
    resolveDynamic(primary?.label, job) ||
    resolveDynamic(primary?.tooltip, job) ||
    (primary?.key ? t(`actions.primary.${primary.key}`) : "");

  /* Variante visual do botão primário. "ghost" é usada para ações primárias
     de consulta (sem pulso, sem cor sólida). O estado de "ação humana"
     ignora a ghost e força o botão preenchido com pulso. */
  const primaryVariant = resolveDynamic(primary?.variant, job);
  const isGhostPrimary = primaryVariant === "ghost" && !isHumanAction;

  const hasDivider = primary && (secondary.length > 0 || menuItems.length > 0);
  const hasSecondaryToMenuDivider =
    secondary.length > 0 && menuItems.length > 0;

  return (
    <Box className={classes.root}>
      {primary && (
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={primary.icon}
          onClick={(e) => handleClick(e, primary)}
          disabled={primary.disabled && primary.disabled(job)}
          className={`${classes.primaryButton} ${
            isGhostPrimary ? classes.primaryButtonGhost : primaryKeyClass || ""
          } ${
            isHumanAction ? classes.primaryButtonHumanAction : ""
          }`.trim()}
        >
          {primaryLabel}
        </Button>
      )}

      {hasDivider && <Divider orientation="vertical" flexItem className={classes.divider} />}

      {secondary.map((action) => {
        const secTooltip = resolveDynamic(action.tooltip, job);
        const secLabel = resolveDynamic(action.label, job);
        return (
          <Tooltip
            key={action._key}
            title={secTooltip || secLabel || ""}
            arrow
            placement="top"
          >
            <span>
              <IconButton
                size="small"
                className={classes.secondaryIconButton}
                onClick={(e) => handleClick(e, action)}
                disabled={action.disabled && action.disabled(job)}
                aria-label={secTooltip || secLabel || action._key}
              >
                {action.icon}
              </IconButton>
            </span>
          </Tooltip>
        );
      })}

      {hasSecondaryToMenuDivider && (
        <Divider orientation="vertical" flexItem className={classes.divider} />
      )}

      {menuItems.length > 0 && (
        <>
          <Tooltip title={t("actions.moreActions")} arrow placement="top">
            <IconButton
              size="small"
              className={classes.menuButton}
              onClick={handleMenuOpen}
              aria-label={t("actions.moreActions")}
              aria-haspopup="true"
              aria-expanded={menuAnchor ? "true" : "false"}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ className: classes.menuPaper }}
          >
            {menuItems.map((action, idx) => {
              const isDanger = action._tier === "danger";
              const previousIsDanger =
                idx > 0 && menuItems[idx - 1]._tier === "danger";
              const showDividerBefore = isDanger && !previousIsDanger;
              const label =
                resolveDynamic(action.label, job) ||
                resolveDynamic(action.tooltip, job) ||
                t(`actions.${action.key}`, action.key || "");
              return (
                <React.Fragment key={action._key}>
                  {showDividerBefore && <Divider />}
                  <MenuItem
                    onClick={(e) => handleMenuItemClick(e, action)}
                    disabled={action.disabled && action.disabled(job)}
                    className={`${classes.menuItem} ${
                      isDanger ? classes.menuItemDanger : ""
                    }`.trim()}
                  >
                    <ListItemIcon>{action.icon}</ListItemIcon>
                    <ListItemText primary={label} />
                  </MenuItem>
                </React.Fragment>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default JobActionsCell;
